import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, Spinner } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { MaintenanceTaskCard } from '@/components/admin/maintenance/MaintenanceTaskCard.tsx';
import {
  activeRunOf,
  cancelMaintenanceRun,
  listMaintenanceTasks,
  runMaintenanceTask,
  type MaintenanceRun,
} from '@/lib/maintenance.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';

export const Route = createFileRoute('/_app/admin/maintenance')({
  staticData: { orgScope: 'none' },
  component: MaintenancePage,
});

/** How often the page re-reads while a run is going. */
const POLL_MS = 3000;

/**
 * The one-off jobs that rewrite user data in bulk, and their history.
 *
 * Started by hand and never on a schedule: each of these exists because a
 * change to the product left the existing rows behind, so somebody has to
 * decide it is time. The page polls only while something is running — a
 * finished list does not change on its own.
 */
export function MaintenancePage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [userIds, setUserIds] = useState<Record<string, UserOption | null>>({});

  const { data: tasks, isPending } = useQuery({
    queryKey: ['maintenanceTasks'],
    queryFn: listMaintenanceTasks,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((task) => activeRunOf(task) !== null)
        ? POLL_MS
        : false,
  });

  const start = useMutation({
    mutationFn: (task: string) => runMaintenanceTask(task, userIds[task]?.id ?? null),
    onSuccess: async () => {
      setError('');
      await queryClient.invalidateQueries({ queryKey: ['maintenanceTasks'] });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to start the task')),
  });

  const cancel = useMutation({
    mutationFn: (run: MaintenanceRun) => cancelMaintenanceRun(run.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenanceTasks'] });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to cancel the run')),
  });

  return (
    <>
      <PageHeader title='Maintenance' />

      <PageBody className='flex flex-col gap-5'>
        {error && <p className='text-sm text-danger'>{error}</p>}

        {isPending && <Spinner className='size-5 text-ink-muted' />}

        {!isPending && (tasks ?? []).length === 0 && (
          <p className='text-sm text-ink-muted'>No maintenance tasks are registered.</p>
        )}

        {(tasks ?? []).map((task) => (
          <MaintenanceTaskCard
            key={task.name}
            task={task}
            user={userIds[task.name] ?? null}
            onUserChange={(user) =>
              setUserIds((current) => ({ ...current, [task.name]: user }))
            }
            busy={start.isPending}
            running={start.isPending && start.variables === task.name}
            onRun={() => start.mutate(task.name)}
            onCancel={(run) => cancel.mutate(run)}
          />
        ))}
      </PageBody>
    </>
  );
}
